import * as $ from 'jquery';

import { Module } from 'lib/vendor/terrific/Module';
import { createAnimation, Easing, AnimationHandle } from '../../util/createAnimation';
import { Hook } from 'lib/vendor/terrific/Sandbox';

// How much below an integer value the device pixel ratio can fall when still the larger image is loaded.
const RETINA_THRESHOLD = 0.8;

interface SpriteDefinition {
	[scaleFactor : number] : string;
}

/**
 * ImageAnimation module.
 * Used for animating the Swisscom logo.
 */
export default class ImageAnimation extends Module {
	// How many frames the sprite contains
	private frames = 100;
	// How long the complete animation should last (in ms)
	private  duration = 3000;
	// How often to repeat the animation
	private repeat = Infinity;
	// When to start loading the sprite. Valid values are 'ready' (on DOMContentReady) or 'load' (on window.load)
	private loadPoint : 'load' | 'ready' = 'load';
	// The sprite image sources
	private sprites! : SpriteDefinition;
	// The easing function to animate with
	private easing! : Easing | string;
	// The document which this animation belongs to
	private document = document;
	// The handle returned by createAnimation
	private animationHandle? : AnimationHandle<number>;
	// The image element that contains the static version of the logo
	private $image! : JQuery;
	// The image element used to load the sprite
	private $spriteImage! : JQuery;

	// Do nothing on “on” so initialization is quick
	public on(callback : Hook) {
		this.document = this.$ctx[0].ownerDocument || document;
		callback();
	}

	// Read options and start loading the sprite image on “after”
	public after() {
		this.$image = this.$ctx.children('img');
		this.frames = Number(this.$ctx.data('frames')) || this.frames;
		this.duration = Number(this.$ctx.data('duration')) || this.duration;
		this.repeat = Number(this.$ctx.data('repeat')) || this.repeat;
		this.loadPoint = this.$ctx.data('loadPoint') || this.loadPoint;

		this.sprites = {};
		const sprite1x = this.$ctx.data('sprite') || this.$image.prop('src').replace(/\.([^\/]+)$/, '-sprites.$1');
		this.sprites[1] = sprite1x;

		let size = 2;
		while(this.$ctx.data(`sprite-${size}x`)) {
			this.sprites[size] = this.$ctx.data(`sprite-${size}x`);
			size += 1;
		}

		this.easing = this.$ctx.data('easing');
		this.log('Initialized', this);

		this.setUpSpriteLoader();
	}

	private setUpSpriteLoader() {
		this.log('Attaching sprite loader for ', this.loadPoint);

		if(this.loadPoint === 'load') {
			if(this.document.readyState === 'complete') {
				this.loadSprite();
			} else {
				$(window).on('load', this.loadSprite.bind(this));
			}
		} else {
			if (this.document.readyState === 'complete' || this.document.readyState === 'interactive') {
				this.loadSprite();
			} else {
				$(this.document).on('ready', this.loadSprite.bind(this));
			}
		}
	}
	private spriteSrc() {
		const ratio = Math.max(
			window.devicePixelRatio
			|| (window as any).webkitDevicePixelRatio
			|| (window as any).mozDevicePixelRatio
			|| (window as any).msDevicePixelRatio
			|| 1,
			1,
		);

		let intRatio = Math.floor(ratio + RETINA_THRESHOLD);
		while(!(intRatio in this.sprites)) {
			intRatio -= 1;
		}

		this.log('Using sprite', this.sprites[intRatio], 'with ratio', intRatio, 'for ratio', ratio, 'from', this.sprites);

		return this.sprites[intRatio];
	}

	private loadSprite() {
		const src = this.spriteSrc();
		this.log('Loading sprite', src);
		const spriteImage = this.document.createElement('img');
		(spriteImage as any).referrer = 'no-referrer';
		this.$spriteImage = $(spriteImage);
		this.$spriteImage.addClass('animated-image');
		this.$spriteImage.addClass(this.$image[0].className);
		this.$spriteImage.removeClass('still-image');
		this.$spriteImage.on('load', this.spriteLoaded.bind(this));
		spriteImage.src = src;
	}
	private ensureStillImageLoaded(callback : () => void) {
		if(this.$image.prop('complete')) {
			callback();
		} else {
			this.$image.on('load', callback);
		}
	}
	private spriteLoaded() {
		this.log('Loaded sprite');
		this.ensureStillImageLoaded(this.animationReady.bind(this));
	}
	private animationReady() {
		this.log('Begin animating');
		this.animationHandle = createAnimation({
			animator: this.animator.bind(this),
			callback: this.animationEnded.bind(this),
			duration: this.duration,
			easing: this.easing,
			endValue: this.frames - 1,
			startValue: 0,
		});
		this.repeatAnimation();
	}
	private animator(_progress : number, sprite : number) {
		// Make sure the index is an integer
		sprite = Math.floor(sprite);
		const offset = sprite * (Number(this.$ctx.data('width')) || this.$image.prop('width'));
		this.$spriteImage.css('marginLeft', `-${offset}px`);
	}
	private repeatAnimation() {
		if(!this.animationHandle) {
			return;
		}
		if(this.repeat > 0) {
			this.startAnimation();
			this.repeat -= 1;
		}
	}
	private startAnimation() {
		if(!this.animationHandle) {
			return;
		}
		this.animationHandle.start();
		this.$ctx[0].replaceChild(this.$spriteImage[0], this.$image[0]);
		this.$ctx.addClass('animating');
	}
	private animationEnded(successfully : boolean | null) {
		this.$ctx[0].replaceChild(this.$image[0], this.$spriteImage[0]);
		this.$ctx.removeClass('animating');
		if(successfully) {
			this.repeatAnimation();
		}
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/generic/header/Tc.Module.ImageAnimation.ts